import { useState, useEffect } from "react";
import axiosInstance from "@/config/axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Save, Mic, Music, Globe, MessageSquare, BarChart3, RefreshCw, CheckCircle, XCircle, ExternalLink, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ELEVENLABS_FEATURES = [
  {
    id: "tts",
    name: "Text-to-Speech",
    icon: Mic,
    description: "Convert text to natural-sounding speech",
    endpoints: [
      "/v1/text-to-speech",
      "/v1/text-to-speech/stream",
      "/v1/text-to-speech/with-timestamps"
    ]
  },
  {
    id: "voices",
    name: "Voice Management",
    icon: Mic,
    description: "Clone, design, and manage custom voices",
    endpoints: [
      "/v1/voices",
      "/v1/voices/add",
      "/v1/voice-generation/create-previews"
    ]
  },
  {
    id: "dubbing",
    name: "Audio Dubbing",
    icon: Globe,
    description: "Translate and localize audio content (70+ languages)",
    endpoints: [
      "/v1/dubbing",
      "/v1/dubbing/list",
      "/v1/dubbing/{id}/audio"
    ]
  },
  {
    id: "music",
    name: "Music Generation",
    icon: Music,
    description: "Generate AI music from text prompts",
    endpoints: [
      "/v1/music/compose",
      "/v1/music/stream",
      "/v1/music/plan"
    ]
  },
  {
    id: "stt",
    name: "Speech-to-Text",
    icon: MessageSquare,
    description: "Transcribe audio with enhanced accuracy",
    endpoints: [
      "/v1/speech-to-text"
    ]
  },
  {
    id: "analytics",
    name: "Usage Analytics",
    icon: BarChart3,
    description: "Track character usage and subscription",
    endpoints: [
      "/v1/usage/stats",
      "/v1/user/subscription",
      "/v1/user"
    ]
  }
];

export default function ElevenLabsConfiguration() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  
  const [formData, setFormData] = useState({
    api_key: "",
    default_voice_id: "",
    // Feature toggles
    tts_enabled: true,
    voice_cloning_enabled: false,
    dubbing_enabled: false,
    music_enabled: false,
    stt_enabled: false
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(`${API}/settings/communication/elevenlabs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setFormData({
        api_key: response.data.api_key || "",
        default_voice_id: response.data.default_voice_id || "",
        tts_enabled: response.data.tts_enabled !== undefined ? response.data.tts_enabled : true,
        voice_cloning_enabled: response.data.voice_cloning_enabled || false,
        dubbing_enabled: response.data.dubbing_enabled || false,
        music_enabled: response.data.music_enabled || false,
        stt_enabled: response.data.stt_enabled || false
      });
    } catch (error) {
      console.error("Failed to fetch settings:", error);
    }
  };

  const handleTestConnection = async () => {
    if (!formData.api_key) {
      toast.error("Please enter your API key first");
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.post(
        `${API}/settings/communication/test-elevenlabs`,
        { api_key: formData.api_key },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setTestResult({
        success: true,
        message: response.data.message,
        userInfo: response.data.user_info
      });
      toast.success(response.data.message);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Connection test failed";
      setTestResult({ success: false, message: errorMsg });
      toast.error(errorMsg);
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!formData.api_key) {
      toast.error("Please enter your API key");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axiosInstance.put(`${API}/settings/communication/elevenlabs`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("ElevenLabs settings saved successfully");
      navigate("/settings/communication");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8" data-testid="elevenlabs-configuration-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate("/settings/communication")}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-slate-800 mb-2">ElevenLabs Configuration</h1>
            <p className="text-slate-600">Configure ElevenLabs AI audio services and endpoints</p>
          </div>
        </div>

        {/* API Key Section */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mic className="w-5 h-5" />
              API Authentication
            </CardTitle>
            <CardDescription>
              Configure your ElevenLabs API key to access all services
              <a
                href="https://elevenlabs.io/app/settings/api-keys"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 ml-2 text-indigo-600 hover:text-indigo-700 font-medium"
              >
                Get API Key <ExternalLink className="w-3 h-3" />
              </a>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="api_key">API Key *</Label>
              <Input
                id="api_key"
                type="password"
                value={formData.api_key}
                onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                placeholder="sk_..."
                className="h-11 border-slate-300 font-mono text-sm"
              />
              <p className="text-xs text-slate-500 mt-1">
                Your API key is encrypted and stored securely
              </p>
            </div>

            <div>
              <Label htmlFor="default_voice_id">Default Voice ID (Optional)</Label>
              <Input
                id="default_voice_id"
                value={formData.default_voice_id}
                onChange={(e) => setFormData({ ...formData, default_voice_id: e.target.value })}
                placeholder="21m00Tcm4TlvDq8ikWAM"
                className="h-11 border-slate-300 font-mono text-sm"
              />
              <p className="text-xs text-slate-500 mt-1">
                Find voice IDs in your{" "}
                <a
                  href="https://elevenlabs.io/app/voice-library"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:underline"
                >
                  Voice Library
                </a>
              </p>
            </div>

            <div className="border-t pt-4">
              <Button
                onClick={handleTestConnection}
                disabled={testing || !formData.api_key}
                className="w-full"
                variant="outline"
              >
                {testing ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Testing API Connection...
                  </>
                ) : (
                  <>
                    <Mic className="w-4 h-4 mr-2" />
                    Test ElevenLabs Connection
                  </>
                )}
              </Button>

              {testResult && (
                <div className={`mt-4 flex items-start gap-2 p-4 rounded ${testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  {testResult.success ? <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" /> : <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />}
                  <div className="flex-1">
                    <p className={`text-sm font-medium ${testResult.success ? 'text-green-900' : 'text-red-900'}`}>
                      {testResult.message}
                    </p>
                    {testResult.success && testResult.userInfo && (
                      <div className="mt-2 space-y-1 text-xs text-green-800">
                        <p><strong>User ID:</strong> {testResult.userInfo.user_id}</p>
                        <p><strong>Subscription:</strong> {testResult.userInfo.subscription_tier || 'Free'}</p>
                        {testResult.userInfo.character_count !== undefined && (
                          <p><strong>Characters Used:</strong> {testResult.userInfo.character_count.toLocaleString()}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Available Features */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Available ElevenLabs Features</CardTitle>
            <CardDescription>
              Overview of all API endpoints and services you have access to with your API key
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="all" className="w-full">
              <TabsList className="grid w-full grid-cols-7">
                <TabsTrigger value="all">All</TabsTrigger>
                {ELEVENLABS_FEATURES.map((feature) => (
                  <TabsTrigger key={feature.id} value={feature.id} className="text-xs">
                    {feature.name.split(" ")[0]}
                  </TabsTrigger>
                ))}
              </TabsList>

              <TabsContent value="all" className="space-y-4 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {ELEVENLABS_FEATURES.map((feature) => {
                    const Icon = feature.icon;
                    return (
                      <Card key={feature.id} className="border-slate-200">
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-2">
                              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                                <Icon className="w-5 h-5 text-indigo-600" />
                              </div>
                              <div>
                                <CardTitle className="text-base">{feature.name}</CardTitle>
                              </div>
                            </div>
                          </div>
                          <CardDescription className="text-sm">{feature.description}</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-1">
                            <Label className="text-xs font-semibold text-slate-600">API Endpoints:</Label>
                            {feature.endpoints.map((endpoint, idx) => (
                              <div key={idx} className="flex items-center gap-2 text-xs">
                                <code className="bg-slate-100 px-2 py-1 rounded text-slate-700 flex-1">
                                  {endpoint}
                                </code>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </TabsContent>

              {ELEVENLABS_FEATURES.map((feature) => {
                const Icon = feature.icon;
                return (
                  <TabsContent key={feature.id} value={feature.id} className="mt-4">
                    <Card className="border-indigo-200">
                      <CardHeader>
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                            <Icon className="w-6 h-6 text-indigo-600" />
                          </div>
                          <div>
                            <CardTitle>{feature.name}</CardTitle>
                            <CardDescription>{feature.description}</CardDescription>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <Label className="text-sm font-semibold text-slate-700 mb-2 block">Available Endpoints:</Label>
                          <div className="space-y-2">
                            {feature.endpoints.map((endpoint, idx) => (
                              <div key={idx} className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
                                <code className="text-sm text-slate-800 font-mono flex-1">{endpoint}</code>
                                <a
                                  href={`https://elevenlabs.io/docs/api-reference${endpoint.split('?')[0]}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-indigo-600 hover:text-indigo-700"
                                  title="View documentation"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </a>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <p className="text-sm text-blue-900">
                            <strong>Note:</strong> All endpoints require your API key for authentication. 
                            Use the official{" "}
                            <a
                              href="https://elevenlabs.io/docs/api-reference/overview"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="underline font-medium"
                            >
                              ElevenLabs API documentation
                            </a>
                            {" "}for detailed parameters and examples.
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                );
              })}
            </Tabs>
          </CardContent>
        </Card>

        {/* Quick Links */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Quick Links & Resources</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <a
                href="https://elevenlabs.io/docs/quickstart"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 p-3 border border-slate-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
              >
                <ExternalLink className="w-4 h-4 text-indigo-600" />
                <span className="text-sm font-medium">Quick Start</span>
              </a>
              <a
                href="https://elevenlabs.io/docs/api-reference/overview"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 p-3 border border-slate-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
              >
                <ExternalLink className="w-4 h-4 text-indigo-600" />
                <span className="text-sm font-medium">API Reference</span>
              </a>
              <a
                href="https://elevenlabs.io/app/voice-library"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 p-3 border border-slate-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
              >
                <ExternalLink className="w-4 h-4 text-indigo-600" />
                <span className="text-sm font-medium">Voice Library</span>
              </a>
              <a
                href="https://elevenlabs.io/app/usage"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 p-3 border border-slate-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
              >
                <ExternalLink className="w-4 h-4 text-indigo-600" />
                <span className="text-sm font-medium">Usage Stats</span>
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end gap-4">
          <Button
            variant="outline"
            onClick={() => navigate("/settings/communication")}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading || !formData.api_key}
            className="bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700"
          >
            <Save className="w-4 h-4 mr-2" />
            {loading ? "Saving..." : "Save Settings"}
          </Button>
        </div>
      </div>
    </div>
  );
}
